import { CSSProperties, FC, ReactNode, useCallback } from 'react';
import { useSelect } from 'downshift';
import { Field, useField, useForm, FieldProps, FieldRenderProps } from 'react-final-form';
import theme from 'config/theme';
import { BodySmall } from 'components/Typography';
import Icon, { IconNames } from 'components/Icon';
import { SelectFieldContainer, SelectOptionsContainer } from './styled';
import { InputWithFloatingLabel } from 'components/Form/InputWithFloatingLabel';
import classNames from 'classnames';
import { Row } from 'components/Row';

const getToggleIconStyles = (showOptions: boolean) =>
  ({
    background: showOptions ? theme.colors.oathBlue : theme.colors.inkBlue,
    borderRadius: '30px',
    cursor: 'pointer',
    position: 'absolute',
    right: '7px',
    top: '8px',
  }) as CSSProperties;

type ValueType = string | number;

interface Option {
  label: ReactNode;
  value: ValueType;
}

interface Props extends FieldProps<HTMLInputElement, FieldRenderProps<any>> {
  label?: ReactNode;
  name: string;
  options: Option[];
}

const itemToString = (option: Option | null) =>
  typeof option?.label === 'string' ? option.label : String(option?.value ?? '');

export const SelectField: FC<Props> = ({
  label,
  name,
  options,
  validate,
  onChange,
  ...fieldProps
}) => {
  const { change: changeForm } = useForm();
  const {
    input: { value, onBlur, onFocus },
    meta: { error, submitError, touched },
  } = useField(name, {
    subscription: { value: true, error: true, submitError: true, touched: true },
  });
  const activeOption = options.find(option => option.value === value) ?? null;
  const inactiveOptions = options.filter(option => option.value !== value);

  const handleSelectedItemChange = useCallback(
    ({ selectedItem }: { selectedItem: Option | null | undefined }) => {
      if (!selectedItem) return;
      if (onChange) onChange(selectedItem.value);
      changeForm(name, selectedItem.value);
    },
    [changeForm, name, onChange]
  );

  const { isOpen, highlightedIndex, getToggleButtonProps, getMenuProps, getItemProps } =
    useSelect<Option>({
      items: inactiveOptions,
      itemToString,
      selectedItem: null,
      onSelectedItemChange: handleSelectedItemChange,
    });

  const toggleAriaLabel = typeof label === 'string' ? label : undefined;
  const toggleProps = getToggleButtonProps({
    'aria-label': toggleAriaLabel,
    onBlur,
    onFocus,
  });

  return (
    <SelectFieldContainer>
      <Field component="select" name={name} hidden validate={validate} {...fieldProps}>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Field>

      <InputWithFloatingLabel
        className={classNames({ active: isOpen, error: touched && (error || submitError) })}
        error={touched && (error || submitError)}
        label={label}
        value={activeOption?.value}
      >
        <Row {...toggleProps} $pressable={true} style={{ position: 'relative' }}>
          <BodySmall className="input" style={{ width: '100%' }}>
            {activeOption?.label || (!label && '--')}
          </BodySmall>
          <Icon
            name={isOpen ? IconNames.ChevronUp : IconNames.ChevronDown}
            color={theme.colors.white}
            size={30}
            style={getToggleIconStyles(isOpen)}
            aria-hidden="true"
          />
        </Row>

        <div {...getMenuProps()} style={{ display: isOpen ? 'block' : 'none' }}>
          {isOpen && (
            <SelectOptionsContainer>
              {inactiveOptions.map((option, index) => (
                <div
                  className={classNames('select-option', {
                    highlighted: highlightedIndex === index,
                  })}
                  key={option.value}
                  {...getItemProps({ item: option, index })}
                >
                  <BodySmall $color="inherit">{option.label}</BodySmall>
                </div>
              ))}
            </SelectOptionsContainer>
          )}
        </div>
      </InputWithFloatingLabel>
    </SelectFieldContainer>
  );
};
